import type { TUuid } from '../types/common';
import type {
  TFacilityIdsByType,
  TFacilityTree,
  TFacilityTreeStructureNode,
} from '../types/facilityTree';
import {
  EUserFacilityRelationType,
  type TFacilityRelationRef,
} from '../types/facilityUserRelation';
import { refId } from './common';
import { findTreeNodeById } from './facilityTree';

export const groupFacilityIdsByType = (
  relations: ReadonlyArray<TFacilityRelationRef | null> | null | undefined,
): TFacilityIdsByType => {
  const buckets: TFacilityIdsByType = {
    [EUserFacilityRelationType.ADMINISTRATE]: new Set<TUuid>(),
    [EUserFacilityRelationType.READ]: new Set<TUuid>(),
  };
  relations?.forEach((relation) => {
    const id = refId(relation?.facility_id);
    if (relation?.type && id && buckets[relation.type]) {
      buckets[relation.type].add(id);
    }
  });
  return buckets;
};

export const attachFacilityPermissions = (
  node: TFacilityTreeStructureNode,
  isMainFacilityAdmin: boolean,
  permittedFacilityIds: TFacilityIdsByType,
): TFacilityTree => {
  const administrate = isMainFacilityAdmin
    || permittedFacilityIds[EUserFacilityRelationType.ADMINISTRATE].has(node.id);
  const read = administrate || permittedFacilityIds[EUserFacilityRelationType.READ].has(node.id);

  return {
    ...node,
    permissions: { administrate, read },
    children: node.children.map((child) =>
      attachFacilityPermissions(child, isMainFacilityAdmin, permittedFacilityIds)),
  };
};

export const getFacilityTreeIds = (
  node: TFacilityTree | null | undefined,
  onlyAccessible = false,
): TUuid[] => {
  if (!node?.id) {
    return [];
  }
  const descendantIds = node.children.flatMap((child) => getFacilityTreeIds(child, onlyAccessible));
  return !onlyAccessible || node.permissions.read ? [node.id, ...descendantIds] : descendantIds;
};

export const getFacilityScopeIds = (
  facilityTree: TFacilityTree | null | undefined,
  facilityId: TUuid,
  onlyAccessible = true,
): TUuid[] => {
  const node = findTreeNodeById(facilityTree, facilityId);
  return node ? getFacilityTreeIds(node, onlyAccessible) : [facilityId];
};
